// ============================================================
// EditTenantDialog.js
// Modal form on the Tenant detail page: name + identity provider +
// client tenant directory ID. Submit → PATCH /api/mgmt/admin/tenants/{id}.
//
// Exists because, until 2026-09-03, no tenant edit path existed at
// all: a prospect stamped with the provisioning dialog's old m365
// default could never be corrected, and a client that later migrated
// to Microsoft 365 could not be re-described. The provider is a
// commercial fact (which path the client is on), not a runtime switch
// — sign-in is unaffected by anything saved here.
//
// Field errors: the backend names the offending field (`field`) on
// 400 and on the 409 federation_active refusal, so both land inline
// under the control that caused them.
// ============================================================

import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../components/Modal';
import Field from '../components/Field';
import { updateTenant } from '../api/tenants';
import { translateError } from '../utils/errorMessages';
import { toast } from '../components/Toaster';
import { IDENTITY_PROVIDER_OPTIONS } from './CreateTenantDialog';
import './CreateTenantDialog.css';

export default function EditTenantDialog({ tenant, isOpen, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    tenantName: tenant.tenantName || '',
    identityProvider: tenant.identityProvider || 'other',
    externalTenantId: tenant.externalTenantId || '',
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  function handleClose() {
    if (isSubmitting) return;
    onClose();
  }

  function validate() {
    const e = {};
    if (!form.tenantName.trim()) e.tenantName = 'Tenant name is required.';
    if (form.tenantName.length > 200) e.tenantName = 'Tenant name must be 200 characters or fewer.';
    if (form.externalTenantId && form.externalTenantId.length > 100) {
      e.externalTenantId = 'Client tenant directory (tenant) ID must be 100 characters or fewer.';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  // Send only what changed; an unchanged submit is a no-op close.
  function buildPatch() {
    const body = {};
    const name = form.tenantName.trim();
    if (name !== (tenant.tenantName || '')) body.tenantName = name;
    if (form.identityProvider !== tenant.identityProvider) body.identityProvider = form.identityProvider;
    const ext = form.externalTenantId.trim();
    if (ext !== (tenant.externalTenantId || '')) body.externalTenantId = ext === '' ? null : ext;
    return body;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!validate()) return;
    const body = buildPatch();
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const updated = await updateTenant(tenant.tenantId, body);
      toast.success('Tenant updated');
      onSaved(updated);
      onClose();
    } catch (err) {
      if (err && err.field) {
        setErrors({ [err.field]: err.detail || translateError(err) });
      } else {
        setSubmitError(translateError(err));
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit tenant" closeDisabled={isSubmitting}>
      <form className="create-tenant-form" onSubmit={handleSubmit} noValidate>
        {submitError && (
          <div className="create-tenant-form__banner" role="alert">{submitError}</div>
        )}

        <Field label="Tenant name" error={errors.tenantName} required>
          <input
            type="text"
            value={form.tenantName}
            maxLength={200}
            disabled={isSubmitting}
            onChange={(e) => setForm({ ...form, tenantName: e.target.value })}
            autoFocus
          />
        </Field>

        <Field
          label="Identity provider"
          error={errors.identityProvider}
          required
          help="Which email / identity system the organisation runs. Changing this does not change how anyone signs in; federation is set up separately on the Federation tab and is Microsoft 365 only."
        >
          <select
            value={form.identityProvider}
            disabled={isSubmitting}
            onChange={(e) => setForm({ ...form, identityProvider: e.target.value })}
          >
            {IDENTITY_PROVIDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>

        <Field
          label="Client tenant directory (tenant) ID"
          error={errors.externalTenantId}
          help="Optional. The client's M365 / Workspace directory (tenant) ID, if known. Clear the field to remove it."
        >
          <input
            type="text"
            value={form.externalTenantId}
            maxLength={100}
            disabled={isSubmitting}
            onChange={(e) => setForm({ ...form, externalTenantId: e.target.value })}
          />
        </Field>

        <div className="create-tenant-form__actions">
          <button
            type="button"
            className="create-tenant-form__cancel"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="create-tenant-form__submit"
            disabled={isSubmitting}
          >
            {isSubmitting && <Loader2 size={14} className="create-tenant-form__spinner" />}
            Save changes
          </button>
        </div>
      </form>
    </Modal>
  );
}
